import { apiClient } from '@/lib/api/client'

/** Release stages, widest exposure last. See supabase/migrations/040_feature_flags.sql. */
export type FeatureStage = 'off' | 'internal' | 'preview' | 'ga'

export interface FeatureFlag {
  key: string
  label: string
  description: string | null
  stage: FeatureStage
  grantCount: number
  createdAt: string
  updatedAt: string
}

export interface FeatureGrant {
  flagKey: string
  userId: number
  note: string | null
  createdAt: string
  email: string
  firstName: string | null
  lastName: string | null
  artisticName: string | null
}

/**
 * The signed-in account's flags. `staffOnly` is only ever non-empty while a manager is viewing as
 * an artist: the flags that are on because of the manager, which the artist does not have.
 */
export interface MyFeatures {
  features: Record<string, boolean>
  staffOnly: string[]
}

/** The API returns snake_case rows straight from SQL; normalise at the boundary. */
const toFlag = (r: any): FeatureFlag => ({
  key: r.key,
  label: r.label,
  description: r.description ?? null,
  stage: r.stage,
  grantCount: r.grant_count ?? 0,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
})

const toGrant = (r: any): FeatureGrant => ({
  flagKey: r.flag_key,
  userId: r.user_id,
  note: r.note ?? null,
  createdAt: r.created_at,
  email: r.email,
  firstName: r.first_name ?? null,
  lastName: r.last_name ?? null,
  artisticName: r.artistic_name ?? null,
})

export const flagsApi = {
  /** The signed-in account's own flags. Cheap; every screen asks for it. */
  mine: async (): Promise<MyFeatures> => {
    const res = await apiClient.get<{ features: Record<string, boolean>; staffOnly?: string[] }>('/api/features')
    return { features: res.data.features ?? {}, staffOnly: res.data.staffOnly ?? [] }
  },

  list: async (): Promise<FeatureFlag[]> => {
    const res = await apiClient.get<{ data: any[] }>('/api/admin/features')
    return (res.data.data ?? []).map(toFlag)
  },

  setStage: async (key: string, stage: FeatureStage): Promise<void> => {
    await apiClient.patch(`/api/admin/features/${encodeURIComponent(key)}`, { stage })
  },

  grants: async (key: string): Promise<FeatureGrant[]> => {
    const res = await apiClient.get<{ data: any[] }>(`/api/admin/features/${encodeURIComponent(key)}/grants`)
    return (res.data.data ?? []).map(toGrant)
  },

  grant: async (key: string, userId: number, note?: string): Promise<void> => {
    await apiClient.post(`/api/admin/features/${encodeURIComponent(key)}/grants`, { userId, note })
  },

  revoke: async (key: string, userId: number): Promise<void> => {
    await apiClient.delete(`/api/admin/features/${encodeURIComponent(key)}/grants/${userId}`)
  },
}
